/**
 * @fileoverview Customer management hooks using TanStack Query.
 *
 * Provides hooks for:
 * - Fetching customers with filtering and search
 * - Customer CRUD operations
 * - Customer statistics
 *
 * @module hooks/use-customers
 */

import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCurrentTenant } from "./use-tenant";
import { toast } from "sonner";

// ============================================================================
// Types
// ============================================================================

export interface Customer {
  id: string;
  tenant_id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  created_source: string;
  sms_opt_in: boolean;
  call_opt_in: boolean;
  notes: string | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
  // Computed fields (from joins or separate queries)
  total_jobs?: number;
  last_service_date?: string;
  equipment_count?: number;
}

export interface CustomerFilters {
  search?: string;
  tags?: string[];
  hasEquipment?: boolean;
  smsOptIn?: boolean;
  callOptIn?: boolean;
}

export interface CreateCustomerInput {
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  notes?: string;
  tags?: string[];
  sms_opt_in?: boolean;
  call_opt_in?: boolean;
  created_source?: string;
}

export interface UpdateCustomerInput {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  notes?: string;
  tags?: string[];
  sms_opt_in?: boolean;
  call_opt_in?: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const CUSTOMERS_QUERY_KEY = ["customers"];

// ============================================================================
// API Functions
// ============================================================================

async function fetchCustomers(
  tenantId: string,
  filters?: CustomerFilters
): Promise<Customer[]> {
  if (!supabase) return [];

  let query = supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("name", { ascending: true });

  if (filters?.search) {
    query = query.or(
      `name.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,email.ilike.%${filters.search}%,address.ilike.%${filters.search}%`
    );
  }

  if (filters?.tags && filters.tags.length > 0) {
    query = query.overlaps("tags", filters.tags);
  }

  if (filters?.smsOptIn !== undefined) {
    query = query.eq("sms_opt_in", filters.smsOptIn);
  }

  if (filters?.callOptIn !== undefined) {
    query = query.eq("call_opt_in", filters.callOptIn);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

async function fetchCustomerById(tenantId: string, customerId: string): Promise<Customer | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("id", customerId)
    .eq("tenant_id", tenantId)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return data;
}

async function fetchCustomerWithStats(tenantId: string, customerId: string): Promise<Customer | null> {
  if (!supabase) return null;

  // FIX: Fetch customer, jobs, and equipment count in parallel (was 3 sequential queries)
  const [customerResult, jobsResult, equipmentResult] = await Promise.all([
    supabase
      .from("customers")
      .select("*")
      .eq("id", customerId)
      .eq("tenant_id", tenantId)
      .single(),
    supabase
      .from("jobs")
      .select("id, scheduled_at")
      .eq("customer_id", customerId)
      .eq("tenant_id", tenantId)
      .eq("status", "completed")
      .order("scheduled_at", { ascending: false }),
    supabase
      .from("equipment_observations")
      .select("id", { count: "exact", head: true })
      .eq("customer_id", customerId)
      .eq("tenant_id", tenantId)
  ]);

  if (customerResult.error) {
    if (customerResult.error.code === "PGRST116") return null;
    throw customerResult.error;
  }

  if (!customerResult.data) return null;

  return {
    ...customerResult.data,
    total_jobs: jobsResult.data?.length || 0,
    last_service_date: jobsResult.data?.[0]?.scheduled_at || null,
    equipment_count: equipmentResult.count || 0,
  };
}

async function createCustomer(
  tenantId: string,
  input: CreateCustomerInput
): Promise<Customer> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error } = await supabase
    .from("customers")
    .insert({
      tenant_id: tenantId,
      name: input.name,
      phone: input.phone,
      email: input.email,
      address: input.address,
      city: input.city,
      postal_code: input.postal_code,
      notes: input.notes,
      tags: input.tags || [],
      sms_opt_in: input.sms_opt_in ?? true,
      call_opt_in: input.call_opt_in ?? true,
      created_source: input.created_source || "manual",
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateCustomer(
  tenantId: string,
  customerId: string,
  updates: UpdateCustomerInput
): Promise<Customer> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { data, error } = await supabase
    .from("customers")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", customerId)
    .eq("tenant_id", tenantId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteCustomer(tenantId: string, customerId: string): Promise<void> {
  if (!supabase) throw new Error("Supabase client not initialized");

  const { error } = await supabase
    .from("customers")
    .delete()
    .eq("id", customerId)
    .eq("tenant_id", tenantId);

  if (error) throw error;
}

async function searchCustomers(
  tenantId: string,
  searchTerm: string,
  limit: number = 10
): Promise<Customer[]> {
  if (!supabase) return [];

  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .or(
      `name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
    )
    .limit(limit);

  if (error) throw error;
  return data || [];
}

/**
 * Search customers by address for location-based matching.
 * Uses fuzzy matching on street number + name to find properties.
 */
async function searchCustomersByAddress(
  tenantId: string,
  address: string,
  limit: number = 5
): Promise<Customer[]> {
  if (!supabase || !address) return [];

  // Extract key address components for matching
  // e.g., "123 Main Street, San Francisco" -> match on "123 Main"
  const normalized = address.trim().toLowerCase();
  if (normalized.length < 5) return []; // Too short for meaningful match

  // Try to extract street number and first word of street name
  const match = normalized.match(/^(\d+)\s+(\w+)/);
  if (!match) {
    // No street number, just search on full address
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("tenant_id", tenantId)
      .ilike("address", `%${normalized.slice(0, 20)}%`)
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  // Search for matching street number + street name start
  const streetNum = match[1];
  const streetStart = match[2];
  const searchPattern = `${streetNum} ${streetStart}%`;

  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .ilike("address", searchPattern)
    .limit(limit);

  if (error) throw error;
  return data || [];
}

/**
 * Normalize phone number for comparison.
 * Strips all non-digit characters for consistent matching.
 */
function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, "");
}

/**
 * Find a customer by phone number.
 * Uses normalized comparison to handle different formats.
 */
export async function findCustomerByPhone(
  tenantId: string,
  phone: string
): Promise<Customer | null> {
  if (!supabase || !phone) return null;

  const normalizedPhone = normalizePhone(phone);
  if (normalizedPhone.length < 7) return null; // Too short to be valid

  // Try exact match first (with original format)
  const { data: exactMatch } = await supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("phone", phone)
    .limit(1)
    .single();

  if (exactMatch) return exactMatch;

  // Try matching last 10 digits (handles country code variations)
  const last10 = normalizedPhone.slice(-10);
  const { data: customers } = await supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .not("phone", "is", null);

  if (!customers) return null;

  // Find customer where normalized phone ends with same digits
  const match = customers.find((c) => {
    if (!c.phone) return false;
    const customerNormalized = normalizePhone(c.phone);
    return customerNormalized.slice(-10) === last10;
  });

  return match || null;
}

/**
 * Find an existing customer by phone, or create a new one.
 * Used for deduplication when creating jobs from various sources.
 */
export async function findOrCreateCustomer(
  tenantId: string,
  input: {
    name: string;
    phone: string;
    email?: string;
    address?: string;
    created_source?: string;
  }
): Promise<{ customer: Customer; isNew: boolean }> {
  if (!supabase) throw new Error("Supabase client not initialized");

  // Try to find existing customer by phone
  const existing = await findCustomerByPhone(tenantId, input.phone);

  if (existing) {
    // Optionally update missing fields on existing customer
    const updates: Partial<UpdateCustomerInput> = {};
    if (!existing.name && input.name) updates.name = input.name;
    if (!existing.email && input.email) updates.email = input.email;
    if (!existing.address && input.address) updates.address = input.address;

    if (Object.keys(updates).length > 0) {
      const updated = await updateCustomer(tenantId, existing.id, updates);
      return { customer: updated, isNew: false };
    }

    return { customer: existing, isNew: false };
  }

  // Create new customer
  const newCustomer = await createCustomer(tenantId, {
    name: input.name,
    phone: input.phone,
    email: input.email,
    address: input.address,
    created_source: input.created_source || "manual",
  });

  return { customer: newCustomer, isNew: true };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch customers for the current tenant.
 */
export function useCustomers(filters?: CustomerFilters) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, tenant?.id, filters],
    queryFn: () => fetchCustomers(tenant!.id, filters),
    enabled: !!tenant?.id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a single customer by ID.
 */
export function useCustomer(customerId?: string) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, tenant?.id, customerId],
    queryFn: () => fetchCustomerById(tenant!.id, customerId!),
    enabled: !!tenant?.id && !!customerId,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch a customer with service statistics.
 */
export function useCustomerWithStats(customerId?: string) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, tenant?.id, customerId, "stats"],
    queryFn: () => fetchCustomerWithStats(tenant!.id, customerId!),
    enabled: !!tenant?.id && !!customerId,
    staleTime: 60000,
  });
}

/**
 * Hook to search customers (for autocomplete/search inputs).
 */
export function useCustomerSearch(searchTerm: string, limit: number = 10) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, "search", tenant?.id, searchTerm],
    queryFn: () => searchCustomers(tenant!.id, searchTerm, limit),
    enabled: !!tenant?.id && searchTerm.length >= 2,
    staleTime: 10000,
  });
}

/**
 * Hook to search customers by address (for location-based matching).
 * Useful for finding existing customers at a service address.
 */
export function useCustomerAddressSearch(address: string, limit: number = 5) {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, "address-search", tenant?.id, address],
    queryFn: () => searchCustomersByAddress(tenant!.id, address, limit),
    enabled: !!tenant?.id && address.length >= 5,
    staleTime: 10000,
  });
}

/**
 * Hook to create a new customer.
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: (input: CreateCustomerInput) => createCustomer(tenant!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      toast.success("Customer created successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to create customer", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to update a customer.
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: ({
      customerId,
      updates,
    }: {
      customerId: string;
      updates: UpdateCustomerInput;
    }) => updateCustomer(tenant!.id, customerId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      toast.success("Customer updated successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to update customer", {
        description: error.message,
      });
    },
  });
}

/**
 * Hook to delete a customer.
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  const { data: tenant } = useCurrentTenant();

  return useMutation({
    mutationFn: (customerId: string) => deleteCustomer(tenant!.id, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
      toast.success("Customer deleted");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete customer", {
        description: error.message,
      });
    },
  });
}

// ============================================================================
// Stats Hooks
// ============================================================================

/**
 * Hook to get customer statistics for the dashboard.
 */
export function useCustomerStats() {
  const { data: tenant } = useCurrentTenant();

  return useQuery({
    queryKey: [...CUSTOMERS_QUERY_KEY, "stats", tenant?.id],
    queryFn: async () => {
      if (!supabase || !tenant?.id) return null;

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // FIX: Fetch all 4 stats in parallel (was 4 sequential queries)
      const [totalResult, newResult, activeResult, equipmentResult] = await Promise.all([
        // Total customers
        supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", tenant.id),
        // New this month
        supabase
          .from("customers")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", tenant.id)
          .gte("created_at", startOfMonth.toISOString()),
        // Active customers (job in last 30 days)
        supabase
          .from("jobs")
          .select("customer_id")
          .eq("tenant_id", tenant.id)
          .gte("scheduled_at", thirtyDaysAgo.toISOString())
          .not("customer_id", "is", null),
        // Equipment count
        supabase
          .from("equipment_observations")
          .select("id", { count: "exact", head: true })
          .eq("tenant_id", tenant.id)
      ]);

      const uniqueActiveCustomers = new Set(
        activeResult.data?.map((j) => j.customer_id) || []
      );

      return {
        total: totalResult.count || 0,
        newThisMonth: newResult.count || 0,
        activeThisMonth: uniqueActiveCustomers.size,
        equipmentTracked: equipmentResult.count || 0,
      };
    },
    enabled: !!tenant?.id,
    staleTime: 60000,
  });
}

// ============================================================================
// Realtime Subscription Hooks
// ============================================================================

/**
 * Hook to subscribe to real-time customer updates for the current tenant.
 * Automatically invalidates customer queries when records change.
 *
 * @example
 * function CustomersPage() {
 *   useCustomersRealtime();
 *   const { data: customers } = useCustomers();
 *   return <CustomerList customers={customers} />;
 * }
 */
export function useCustomersRealtime() {
  const { data: tenant } = useCurrentTenant();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenant?.id || !supabase) return;

    const channel = supabase
      .channel(`customers:${tenant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "customers",
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          if (import.meta.env.DEV) console.log("[Realtime] Customer change:", payload.eventType);
          queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenant?.id, queryClient]);
}
